import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import api from './axiosConfig';
import ListPageShell from './list/ListPageShell';
import { resolveIcon } from './iconResolver';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import { useFontSize } from './FontSizeContext';
import CustomRecordRecycleBin from './CustomRecordRecycleBin';

// 🎯 شاشة List عامة لأي تطبيق كستم - نفس الكومبوننت ده بيشتغل لأي موديل،
// بيجيب الميتا (اسم الحقول وترتيبها) من /custom-models/{slug} الأول وبعدين
// يرسم الأعمدة ديناميكيًا منها، بالظبط زي طريقة ERPNext في قراءة الـ DocType
// meta قبل الرسم.
const CustomModelList = ({ slug }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const navigate = useNavigate();
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [meta, setMeta] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);
  const itemsPerPage = 20;

  const loadMeta = useCallback(async () => {
    try {
      const res = await api.get(`/custom-models/${slug}`);
      setMeta(res.data);
    } catch (err) {
      toast.error(t('customApps.loadFailed', 'Failed to load this application'));
    }
  }, [slug, t]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/custom-records', {
        params: { model_slug: slug, skip: (currentPage - 1) * itemsPerPage, limit: itemsPerPage, search: searchTerm || undefined },
      });
      setRecords(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(t('customApps.loadFailed', 'Failed to load this application'));
    } finally {
      setLoading(false);
    }
  }, [slug, currentPage, searchTerm, t]);

  useEffect(() => { setCurrentPage(1); setSelectedIds([]); }, [slug]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadRecords(); }, [loadRecords]);

  // 🎯 حذف بالجملة بنفس نسق باقي شاشات الليست في التطبيق (AssetsList.js):
  // تحديد بالتشك بوكس + زرار "Delete Selected" في الـ AppSidebar (اللي
  // بيبعت GLOBAL_BULK_DELETE_HANDLER تلقائيًا لأي appName مش متعرّف)، مش
  // زرار حذف منفرد جوه كل صف - وبتأكيد Swal قبل أي حذف فعلي
  const handleBulkDelete = useCallback(() => {
    const idsToDelete = selectedIdsRef.current;
    if (idsToDelete.length === 0) return;
    Swal.fire({
      ...swalTheme(),
      title: t('common.areYouSure', 'Are you sure?'),
      text: t('customApps.deleteSelectedConfirm', `You are about to delete ${idsToDelete.length} record(s). They'll be moved to the Recycle Bin and can be restored later.`, { count: idsToDelete.length }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.yesDelete', 'Yes, delete them!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      reverseButtons: isRTL,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await Promise.all(idsToDelete.map((id) => api.delete(`/custom-records/${id}`)));
        toast.success(t('common.deleted', 'Deleted'));
        setSelectedIds([]);
        loadRecords();
      } catch (err) {
        toast.error(t('common.deleteFailed', 'Delete failed'));
      }
    });
  }, [t, isRTL, loadRecords]);

  useEffect(() => {
    const handleSidebarDelete = () => {
      if (selectedIdsRef.current.length > 0) {
        handleBulkDelete();
      } else {
        Swal.fire({
          ...swalTheme(),
          icon: 'info',
          title: t('common.alert', 'Alert'),
          text: t('customApps.noSelectionDelete', 'Please select at least one record to delete.'),
          confirmButtonText: t('common.ok', 'OK'),
        });
      }
    };
    window.addEventListener('GLOBAL_BULK_DELETE_HANDLER', handleSidebarDelete);
    return () => window.removeEventListener('GLOBAL_BULK_DELETE_HANDLER', handleSidebarDelete);
  }, [t, handleBulkDelete]);

  useEffect(() => {
    const openRecycleBin = () => setShowRecycleBin(true);
    window.addEventListener('GLOBAL_RECYCLE_BIN_HANDLER', openRecycleBin);
    return () => window.removeEventListener('GLOBAL_RECYCLE_BIN_HANDLER', openRecycleBin);
  }, []);

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.length === records.length ? [] : records.map((r) => r.id)));
  };

  // 🎯 عمود الليست بس للحقول المعلّمة show_in_list، والحقل الـ primary
  // (لو موجود) بيتحط أول عمود دايمًا وبخط عريض - ده اللي بيمثّل "عنوان"
  // السجل زي title_field في ERPNext
  const allFields = meta?.fields || [];
  const primaryField = allFields.find((f) => f.is_primary);
  const listFields = useMemo(() => {
    const visible = allFields.filter((f) => f.show_in_list !== false);
    if (!primaryField) return visible;
    return [primaryField, ...visible.filter((f) => f.id !== primaryField.id)];
  }, [allFields, primaryField]);

  const Icon = resolveIcon(meta?.icon_name);
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  return (
    <div style={{ fontSize: `${fontSize}px` }} className="h-full">
      <ListPageShell
        icon={<Icon size={22} className="text-primary-dark" />}
        title={meta ? (meta.translations?.[i18n.language] || t(`custom.app.${slug}`, meta.label)) : slug}
        loading={loading}
        onRefresh={loadRecords}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t('common.search', 'Search')}
        newButtonLabel={t('common.new', 'New')}
        onNew={() => navigate(`/custom/${slug}/new`)}
        selectedCount={selectedIds.length}
        onClearSelection={() => setSelectedIds([])}
        isRTL={isRTL}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={total}
        onPageChange={setCurrentPage}
      >
        <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
          <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-20">
            <tr>
              <th className="p-3 w-10 text-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 cursor-pointer accent-white"
                  checked={records.length > 0 && selectedIds.length === records.length}
                  onChange={toggleSelectAll}
                />
              </th>
              {listFields.map((f) => (
                <th key={f.id} className="p-3 border-r border-white/10">{f.translations?.[i18n.language] || t(`custom.field.${f.field_name}`, f.label)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map((row) => (
              <tr
                key={row.id}
                className={`border-b hover:bg-primary-soft cursor-pointer transition-colors ${selectedIds.includes(row.id) ? 'bg-primary-soft' : ''}`}
                onClick={() => navigate(`/custom/${slug}/${row.id}`)}
              >
                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="w-4 h-4 cursor-pointer accent-primary"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleSelectRow(row.id)}
                  />
                </td>
                {listFields.map((f) => (
                  <td key={f.id} className={`p-3 text-gray-700 ${f.is_primary ? 'font-black text-ink' : 'font-bold'}`}>
                    {f.field_type === 'boolean'
                      ? (row.data?.[f.field_name] ? '✓' : '')
                      : f.field_type === 'table'
                      ? t('customApps.rowCount', '{{count}} rows', { count: (row.data?.[f.field_name] || []).length })
                      : f.field_type === 'image'
                      ? (row.data?.[f.field_name] ? <img src={row.data[f.field_name]} alt="" className="w-8 h-8 object-cover rounded" /> : '')
                      : f.field_type === 'link'
                      ? (row.data?.[f.field_name]?.label ?? '')
                      : String(row.data?.[f.field_name] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && records.length === 0 && (
              <tr>
                <td colSpan={listFields.length + 1} className="p-8 text-center text-gray-400 font-bold">
                  {t('common.noRecords', 'No records found')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </ListPageShell>
      <CustomRecordRecycleBin
        isOpen={showRecycleBin}
        onClose={() => setShowRecycleBin(false)}
        slug={slug}
        onRestored={loadRecords}
      />
    </div>
  );
};

export default CustomModelList;
